import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getAuction, placeBid } from "../redux/actions/auction";
import { toast } from "react-toastify";
import Header from "../components/Layout/Header";
import Footer from "../components/Layout/Footer";
import {
  HiOutlineLightningBolt,
  HiOutlineClock,
  HiOutlineCurrencyDollar,
  HiOutlineUser,
} from "react-icons/hi";

const AuctionDetailPage = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { auction, isLoading, success, error } = useSelector(
    (state) => state.auctions
  );
  const { user, isAuthenticated } = useSelector((state) => state.user);
  const [bidAmount, setBidAmount] = useState("");
  const [timeLeft, setTimeLeft] = useState("");
  const timerRef = useRef(null);

  useEffect(() => {
    dispatch(getAuction(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (success) {
      toast.success("Bid placed successfully!");
      dispatch({ type: "clearAuctionSuccess" });
      dispatch(getAuction(id));
      setBidAmount("");
    }
    if (error) {
      toast.error(error);
      dispatch({ type: "clearAuctionErrors" });
    }
  }, [success, error]);

  // Countdown timer
  useEffect(() => {
    if (!auction?.endTime || auction.status !== "live") return;

    const updateTimer = () => {
      const diff = new Date(auction.endTime) - new Date();
      if (diff <= 0) {
        setTimeLeft("Auction Ended");
        clearInterval(timerRef.current);
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const secs = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeLeft(
        `${days > 0 ? `${days}d ` : ""}${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`
      );
    };

    updateTimer();
    timerRef.current = setInterval(updateTimer, 1000);
    return () => clearInterval(timerRef.current);
  }, [auction?.endTime, auction?.status]);

  const handlePlaceBid = (e) => {
    e.preventDefault();
    if (!isAuthenticated) return toast.error("Please log in to place a bid");
    const amount = Number(bidAmount);
    if (!amount || amount <= 0) return toast.error("Enter a valid bid amount");
    dispatch(placeBid(id, amount));
  };

  const minBid =
    (auction?.highestBid || auction?.startingPrice || 0) +
    (auction?.minimumIncrement || 1);

  if (isLoading) {
    return (
      <div>
        <Header />
        <div className="flex justify-center py-24">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#086872]" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!auction) {
    return (
      <div>
        <Header />
        <div className="text-center py-24">
          <p className="text-gray-500 text-lg">Auction not found</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Left - Image */}
          <div>
            {auction.product?.images?.[0]?.url ? (
              <img
                src={auction.product.images[0].url}
                alt={auction.title}
                className="w-full rounded-2xl object-cover shadow-lg"
                style={{ maxHeight: "500px" }}
              />
            ) : (
              <div className="w-full h-96 bg-gray-200 rounded-2xl flex items-center justify-center">
                <HiOutlineLightningBolt className="w-20 h-20 text-gray-400" />
              </div>
            )}
          </div>

          {/* Right - Auction Info */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  auction.status === "live"
                    ? "bg-green-100 text-green-800"
                    : auction.status === "scheduled"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {auction.status}
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              {auction.title}
            </h1>
            <p className="text-sm text-gray-500 mb-1">{auction.product?.name}</p>
            {auction.description && (
              <p className="text-gray-600 text-sm mb-6">{auction.description}</p>
            )}

            {/* Countdown Timer */}
            {auction.status === "live" && (
              <div className="bg-red-50 rounded-xl p-4 mb-6 border border-red-100">
                <div className="flex items-center gap-2 mb-1">
                  <HiOutlineClock className="w-5 h-5 text-red-600" />
                  <span className="text-sm font-medium text-red-800">Time Remaining</span>
                </div>
                <p className="text-3xl font-bold text-red-600 font-mono">{timeLeft}</p>
              </div>
            )}

            {/* Pricing Info */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-500 text-sm">Starting Price</span>
                <span className="text-gray-900 font-medium">₹{auction.startingPrice}</span>
              </div>
              <div className="flex justify-between items-center border-t pt-3">
                <span className="text-gray-500 text-sm">Current Highest Bid</span>
                <span className="text-2xl font-bold text-[#086872]">
                  ₹{auction.highestBid || auction.startingPrice}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500 text-sm">Total Bids</span>
                <span className="text-gray-900 font-medium">{auction.totalBids || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500 text-sm">Min Increment</span>
                <span className="text-gray-900">₹{auction.minimumIncrement}</span>
              </div>
            </div>

            {/* Place Bid Form */}
            {auction.status === "live" && (
              <form onSubmit={handlePlaceBid} className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Bid (minimum ₹{minBid})
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">₹</span>
                    <input
                      type="number"
                      min={minBid}
                      value={bidAmount}
                      onChange={(e) => setBidAmount(e.target.value)}
                      className="w-full pl-7 pr-3 py-3 border border-gray-300 rounded-xl text-lg focus:ring-2 focus:ring-[#086872] focus:border-transparent"
                      placeholder={`${minBid} or more`}
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-6 py-3 bg-[#086872] text-white rounded-xl font-semibold hover:bg-[#065a63] transition-colors flex items-center gap-2"
                  >
                    <HiOutlineLightningBolt className="w-5 h-5" />
                    Place Bid
                  </button>
                </div>
              </form>
            )}

            {auction.status === "ended" && auction.winner && (
              <div className="bg-green-50 rounded-xl p-4 mb-6 border border-green-200">
                <p className="text-sm font-medium text-green-800">
                  Winner: {auction.winner?.name || "Announced"}
                </p>
                <p className="text-lg font-bold text-green-700">
                  Winning Bid: ₹{auction.winningBid}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Bid History */}
        {auction.bidHistory && auction.bidHistory.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Bid History</h2>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                {[...auction.bidHistory].reverse().map((bid, i) => (
                  <div
                    key={i}
                    className={`flex justify-between items-center px-4 py-3 ${i === 0 ? "bg-green-50" : "hover:bg-gray-50"}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <HiOutlineUser className="w-4 h-4 text-gray-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {bid.bidder?.name || "Bidder"}
                          {i === 0 && (
                            <span className="ml-2 text-xs text-green-600 font-bold">HIGHEST</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(bid.bidAt).toLocaleString("en-IN")}
                        </p>
                      </div>
                    </div>
                    <span className={`text-lg font-bold ${i === 0 ? "text-green-600" : "text-gray-700"}`}>
                      ₹{bid.amount}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default AuctionDetailPage;
